import { html, render, TemplateResult } from "lit";
import { IFinanzaCargoAplicado } from "../../../data/entidad/Finanza";
import { _SvFinanzasActualizarCargoAsignado } from "../../../data/modulo/FinanzaCargo";
import { _SvFinanzasObtenerCargoAplicadoPorID } from "../../../data/modulo/FinanzasAsignaciones";
import _L from "../../../util/Labels";
import { FormGenerator } from "../../controlD3/Formulario";
import { _InfoIconTemplateWithContent } from "../../controlD3/InfoIcon";
import { _ModalForm } from "../../controlD3/ModalThings/ModalForm";
import { IAsignacionCargoDesc, ICargoRepeticion } from "../../panelCard/Alumno/AlumnosCargosDescuentos";
import { UIUtilTime } from "../../util/Time";
import { _FinanzaCargoFormGetDatosFiscalesFields, EvalFormFacturationVisibility, TFinanzaCargoDataForm, TFinanzaCargoDatosFiscalesForm } from "../FinanzasIngresos/FinanzaCargoForm";

// const PERMISSION_MODULO = Entidad.CModulo.PanelFinanzasCargosDescuentos;
// const PERMISSION_ACTION = Entidad.CAccionPermiso.Editar;

enum CLoadStatus { Success, Error, Loading }

const dateFmt = UIUtilTime._DateFormatStandar;

export class EditarCargoAsignadoConfigFiscalModal {
    /** Información completa de cargo asignado */
    private cargoInfo: IFinanzaCargoAplicado | null = null;
    private loadStatus: CLoadStatus = null;
    private cargoInfoWrapper: HTMLDivElement | null = null;

    private form: FormGenerator<TFinanzaCargoDatosFiscalesForm> | null = null;

    constructor(
        private idAlumno: number,
        private idEscuela: number,
        /** Cargo asignado */
        private cargoAplicado: IAsignacionCargoDesc,
        private asignacionesCargo: ICargoRepeticion[],
        private onUpdateSuccess?: () => void
    ) {
        if (this.validaRepeticiones()) {
            this.openModal();
        }
    }

    private validaRepeticiones(): boolean {
        if (!this.asignacionesCargo?.length) {
            this.asignacionesCargo = [];
            return true;
        }
        // Todas deben pertenecer al cargo
        return this.asignacionesCargo.every(repSelected => this.cargoAplicado.Repeticiones.some(rep => rep.ID === repSelected.ID));
    }

    private openModal() {
        _ModalForm({
            Title: _L("panelfinanzascargosdescuentos.tag_cfg_fiscal_edit"),
            Width: 550,
            GetForm: () => this.getForm(),
            DrawContent: (content, form) => {
                this.loadStatus = CLoadStatus.Loading;

                const cargoInfo = this.renderCargoInfo();
                cargoInfo.style.marginBottom = "var(--padding3)";

                content.node().appendChild(cargoInfo);

                content.node().appendChild(form._Form.node()); // el formulario al final

                _SvFinanzasObtenerCargoAplicadoPorID(this.cargoAplicado.ID).then((res) => {
                    this.cargoInfo = res.Datos;

                    const dataAsignar = { ...this.cargoInfo, IdEscuela: this.idEscuela } as unknown as TFinanzaCargoDatosFiscalesForm;
                    this.form._AsignaData(dataAsignar); // Asignar para poder evaluar Escuela y consultar listas desplegables relacionadas
                    EvalFormFacturationVisibility(this.form as unknown as FormGenerator<TFinanzaCargoDataForm>).then(() => { // Evaluar para mostrar/ocultar campos según la escuela seleccionada
                        this.form._AsignaData(dataAsignar); // Reasignar para mostrar los campos llenos
                        EvalFormFacturationVisibility(this.form as unknown as FormGenerator<TFinanzaCargoDataForm>); // Evaluar para aplicar los cambios según el agrupador seleccionado
                    });

                    this.loadStatus = res.Resultado > 0 && res.Datos ? CLoadStatus.Success : CLoadStatus.Error;
                    this.renderCargoInfo();
                });
            },
            OnAccept: (form) => {
                const data = form._Data;
                return _SvFinanzasActualizarCargoAsignado({
                    IdCargo: this.cargoAplicado.ID,
                    IdAlumno: this.idAlumno,
                    IdEscuela: this.idEscuela,
                    IdAgrupador: data.IdAgrupador,
                    ClaveProdServSAT: data.ClaveProdServSAT,
                    NombreProdServSAT: data.NombreProdServSAT as string,
                    ClaveUnidadSAT: data.ClaveUnidadSAT,
                    NombreUnidadSAT: data.NombreUnidadSAT as string,
                    ComplementoRequerido: data.ComplementoRequerido,
                    IdsAsignacionCargo: this.asignacionesCargo.map(a => a.ID),
                }).then(res => {
                    const resultadosRefrescar = [1 /* exitoso */, -1 /* No encontrado */, -2 /* No asignado */];
                    if (this.onUpdateSuccess && resultadosRefrescar.includes(res.Resultado)) {
                        this.onUpdateSuccess();
                    }
                    return res;
                })
            }
        })
    }

    private renderCargoInfo() {
        if (!this.cargoInfoWrapper) {
            const wrapper = document.createElement("div");
            wrapper.className = "body_informacion";
            this.cargoInfoWrapper = wrapper;
        }

        switch (this.loadStatus) {
            case CLoadStatus.Loading:
                render(html`
                    <label>${_L("general.loading")}</label>
                    <wc-spinner dim="10px" border-width="2px"></wc-spinner>
                `, this.cargoInfoWrapper);
                break;
            case CLoadStatus.Error:
                render(html`
                    <label style="color: var(--color_app_red1);">${_L("general.notif_fail_infosync")}</label>
                `, this.cargoInfoWrapper);
                break;
            case CLoadStatus.Success:
                const cargoAsignado = this.cargoAplicado;
                if (!this.cargoInfo) {
                    break;
                }

                render(html`
                    <p>
                        <b>${_L("panelfinanzascargosdescuentos.tag_cargo")}:</b>
                        ${this.cargoInfo.Nombre}
                    </p>
                    <p>
                        <b>${_L("panelfinanzascargosdescuentos.d_field_strperiodicidad")}:</b>
                        ${cargoAsignado.StrPeriodicidad}
                    </p>
                    <p>
                        <b>${_L("panelfinanzascargosdescuentos.d_field_norepeticiones")}:</b>
                        ${cargoAsignado.NoRepeticiones}
                    </p>
                    <p>
                        <b>${_L(!!cargoAsignado.FechaFin ? "panelfinanzascargosdescuentos.tag_periodo" : "panelfinanzascargosdescuentos.tag_aplicacion")}:</b>
                        ${this.getPeriodoAfectado()}
                        ${_InfoIconTemplateWithContent(this.getPeriodoDetalladoTemplate())}
                    </p>
                `, this.cargoInfoWrapper);
                break;
        }
        return this.cargoInfoWrapper;
    }

    private getForm(): FormGenerator<TFinanzaCargoDatosFiscalesForm> {
        this.form = new FormGenerator<TFinanzaCargoDatosFiscalesForm>();
        this.form._Crear({
            LangModuleKeyInContext: "finanzascargo",
            schema: _FinanzaCargoFormGetDatosFiscalesFields(this.form as unknown as FormGenerator<TFinanzaCargoDataForm>, { IdEscuela: this.idEscuela } as TFinanzaCargoDataForm),
        })
        return this.form;
    }

    /**
     * Representa el periodo afectado, basado en las fechas de las asignaciones seleccionadas o todas las asignaciones.
     */
    private getPeriodoAfectado(): string {
        let asignaciones: ICargoRepeticion[] = this.asignacionesCargo;
        if (!asignaciones?.length) {
            asignaciones = this.cargoAplicado.Repeticiones;
        }
        const fechaInicio = asignaciones.reduce((min, rep) => rep.FechaInicio < min ? rep.FechaInicio : min, asignaciones[0].FechaInicio);
        const fechaFin = asignaciones.reduce((max, rep) => rep.FechaFin > max ? rep.FechaFin : max, asignaciones[0].FechaFin);
        return dateFmt(fechaInicio) + (fechaFin ? " - " + dateFmt(fechaFin) : "");
    }

    private getPeriodoDetalladoTemplate(): TemplateResult<1> {
        let asignaciones: ICargoRepeticion[] = this.asignacionesCargo;
        if (!asignaciones?.length) {
            asignaciones = this.cargoAplicado.Repeticiones;
        }
        const totalAfectados = `${asignaciones.length}/${this.cargoAplicado.Repeticiones.length}`;
        return html`
        <b>${_L("panelfinanzascargosdescuentos.tag_asignaciones_afectadas")} (${totalAfectados}):</b>
        <ul style="margin-left: var(--padding2);">
            ${asignaciones.map(rep => {
            return html`
                <li>
                    ${dateFmt(rep.FechaInicio)} - ${dateFmt(rep.FechaFin)}
                </li>
                `
        })}
        </ul>
        `
    }
}